import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { filterGroupDeliveryDays } from "../utils/groupBasketSchedule";
import useDeliverySlots from "./useDeliverySlots";
import {
  DELIVERY_TYPE,
  SCHEDULED_ORDERS_ONLY,
  buildScheduledDelivery,
  createdOrderIdIn,
  isDeliveryScheduleStale,
  isScheduleRejectionCode,
  rememberPickedSchedule,
  resolveSlotQuery,
  toOrderDeliverySchedule,
} from "../utils/deliverySchedule";
import { extractCreatedOrderId } from "../redux/checkout";

// What a guarded submit resolves with when it stops before order create. Shaped
// like a rejected thunk action so every existing `action.error` check on the
// place-order screens already treats it as "do not charge, do not clear cart".
const stoppedAction = (code) => ({
  type: "deliverySchedule/stopped",
  error: { message: code },
  payload: { code },
  meta: { requestStatus: "rejected" },
});

/**
 * Delivery timing for a web place-order screen: the slot list, the customer's
 * pick, what to send with the order, and the ONE place that asks the customer
 * for a new time (a window that went stale, DELIVERY_SLOT_INVALID, or
 * SCHEDULED_ORDERS_ONLY). Rendered by <DeliveryTimePicker controller={...} />.
 *
 * @param {object}  params
 * @param {string}  params.flow           restaurant | rw | brand | healthcare | inhouse
 * @param {string} [params.vendorId]      the id the screen quotes and orders with
 * @param {string} [params.outletId]
 * @param {boolean}[params.scheduledOnly] open-status says closed now but takes
 *                                        scheduled orders (forces Scheduled)
 */
const useCheckoutDeliverySchedule = ({
  flow,
  vendorId,
  outletId,
  scheduledOnly = false,
  enabled = true,
  earliestWindowStart = null,
} = {}) => {
  const dispatch = useDispatch();
  const query = useMemo(
    () => enabled ? resolveSlotQuery({ flow, vendorId, outletId }) : null,
    [flow, vendorId, outletId, enabled]
  );
  const rawSlots = useDeliverySlots(query);
  const days = useMemo(() => filterGroupDeliveryDays(rawSlots.days, earliestWindowStart), [rawSlots.days, earliestWindowStart]);
  const slots = { ...rawSlots, days, hasWindows: days.some((day) => day.windows.length > 0) };

  const [mode, setMode] = useState(DELIVERY_TYPE.EXPRESS);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStart, setSelectedStart] = useState(null);
  // Latched when order create answers SCHEDULED_ORDERS_ONLY, in case the shop
  // closed after this page loaded.
  const [serverScheduledOnly, setServerScheduledOnly] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const pickerRef = useRef(null);

  // Read once per slot load so a reload never fights the customer's taps.
  const selectedStartRef = useRef(selectedStart);
  selectedStartRef.current = selectedStart;

  useEffect(() => {
    if (slots.status !== "ready") return;
    // Keep the earlier pick while it is still offered.
    const listedDay = selectedStartRef.current
      ? slots.days.find((day) =>
          day.windows.some((w) => w.start === selectedStartRef.current)
        )
      : null;
    if (listedDay) {
      setSelectedDate(listedDay.date);
      return;
    }
    setSelectedStart(null);
    const firstOpen = slots.days.find((day) => day.windows.length > 0);
    setSelectedDate(firstOpen?.date || slots.days[0]?.date || null);
  }, [slots.status, slots.days]);

  const slotsReady = slots.status === "ready";
  // A deployment without /api/delivery-slots offers no windows at all, so
  // there is nothing to force a customer onto. Express must stay available
  // there even if an open-status payload asked for scheduled-only: on a
  // half-rolled-out server (open-status updated, slot route not yet mounted)
  // the alternative is a checkout with Express disabled and no time to pick.
  const schedulingOffered = slots.status !== "unsupported";
  const expressAvailable =
    !(schedulingOffered && (scheduledOnly || serverScheduledOnly)) &&
    !(slots.expressAvailable === false);
  const canSchedule =
    slotsReady && slots.hasWindows && slots.schedulingAvailable !== false;
  // Closed and not taking scheduled orders: nothing to pick. Order create
  // refuses it too; the page's own closed gates explain it.
  const closedNotAccepting =
    slotsReady &&
    slots.expressAvailable === false &&
    slots.schedulingAvailable === false;

  const effectiveMode = expressAvailable ? mode : DELIVERY_TYPE.SCHEDULED;
  const scheduled = effectiveMode === DELIVERY_TYPE.SCHEDULED;
  const activeDay = slots.days.find((day) => day.date === selectedDate) || null;
  const activeWindow =
    activeDay?.windows.find((window) => window.start === selectedStart) || null;
  const schedule =
    scheduled && activeWindow ? buildScheduledDelivery(activeDay, activeWindow) : null;
  const needsWindow = scheduled && !activeWindow;

  const scrollToPicker = () => {
    const node = pickerRef.current;
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const askForNewTime = (title, message) => {
    setMode(DELIVERY_TYPE.SCHEDULED);
    setSelectedStart(null);
    slots.refresh();
    setPrompt({ title, message, canPick: true });
  };

  /**
   * The checks every submit runs before an order is created. Returns a code to
   * stop on (a dialog is now showing), or null to go ahead.
   */
  const blockingCode = () => {
    if (closedNotAccepting) {
      setPrompt({
        title: "Closed right now",
        message: slots.message || "This shop is not taking orders right now.",
        canPick: false,
      });
      return "VENDOR_CLOSED";
    }
    if (needsWindow) {
      setPrompt({
        title: "Pick a delivery time",
        message: expressAvailable
          ? "Choose a delivery window, or switch to express delivery."
          : "This shop is closed right now. Pick a delivery time to order ahead.",
        canPick: canSchedule,
      });
      return "DELIVERY_TIME_REQUIRED";
    }
    if (schedule && isDeliveryScheduleStale(schedule)) {
      askForNewTime(
        "Delivery time has passed",
        "The delivery window you picked is now too soon. Pick another time."
      );
      return "DELIVERY_SLOT_STALE";
    }
    return null;
  };

  /** The server's refusal of a delivery time, handled the one way. */
  const handleRejection = (code, message) => {
    if (!isScheduleRejectionCode(code)) return false;
    if (code === SCHEDULED_ORDERS_ONLY) setServerScheduledOnly(true);
    askForNewTime(
      code === SCHEDULED_ORDERS_ONLY
        ? "Pick a delivery time"
        : "Pick another delivery time",
      message || "That delivery window is no longer available."
    );
    return true;
  };

  /**
   * Create the order with the chosen delivery time. Use in place of
   * `dispatch(thunk(payload))`; resolves with the thunk's action, or a
   * rejected-shaped action when it stopped to ask for a time.
   */
  const submitOrder = async (thunk, payload) => {
    const stop = blockingCode();
    if (stop) return stoppedAction(stop);

    const action = await dispatch(
      thunk({ ...payload, deliverySchedule: toOrderDeliverySchedule(schedule) })
    );
    if (action?.error) {
      handleRejection(action?.payload?.code, action?.payload?.message);
      return action;
    }
    // Carry the pick to the success page, which may read back an order body
    // without `scheduledDelivery`.
    rememberPickedSchedule(schedule, extractCreatedOrderId(action));
    return action;
  };

  /**
   * The same guarded path for a checkout that is a plain request rather than a
   * redux thunk (the brand quote accept). `send(deliverySchedule)` performs the
   * request and resolves with the RESPONSE BODY (or the created order itself);
   * a refusal is read for the server's code whether it arrives as an axios
   * error or as a rejected thunk unwrapped with .unwrap().
   *
   * @returns {Promise<{ok:boolean, stopped?:boolean, code?:string, data?:any, error?:any}>}
   */
  const submitRequest = async (send) => {
    const stop = blockingCode();
    if (stop) return { ok: false, stopped: true, code: stop };
    try {
      const data = await send(toOrderDeliverySchedule(schedule));
      rememberPickedSchedule(schedule, createdOrderIdIn(data));
      return { ok: true, data };
    } catch (error) {
      // An axios error carries the body under `response.data`; a rejected thunk
      // unwrapped with .unwrap() throws the rejectWithValue payload itself.
      const body =
        (error && error.response && error.response.data) || error || {};
      const code = typeof body === "object" ? body.code : null;
      const message = typeof body === "object" ? body.message : null;
      const handled = handleRejection(code, message);
      return { ok: false, code, handled, error };
    }
  };

  return {
    slots,
    query,
    deliverySchedule: toOrderDeliverySchedule(schedule),
    validateSelection: blockingCode,
    handleRejection,
    pickerRef,
    mode: effectiveMode,
    scheduled,
    expressAvailable,
    canSchedule,
    closedNotAccepting,
    scheduledOnly: !expressAvailable && !closedNotAccepting,
    selectedDate,
    selectedStart,
    activeDay,
    activeWindow,
    needsWindow,
    prompt,
    selectExpress: () => setMode(DELIVERY_TYPE.EXPRESS),
    selectScheduled: () => setMode(DELIVERY_TYPE.SCHEDULED),
    selectDay: (date) => {
      setSelectedDate(date);
      setSelectedStart(null);
    },
    selectWindow: (start) => setSelectedStart(start),
    dismissPrompt: () => setPrompt(null),
    pickAnotherTime: () => {
      setPrompt(null);
      setMode(DELIVERY_TYPE.SCHEDULED);
      scrollToPicker();
    },
    switchToExpress: () => {
      setPrompt(null);
      setMode(DELIVERY_TYPE.EXPRESS);
    },
    submitOrder,
    submitRequest,
  };
};

export default useCheckoutDeliverySchedule;
